/**
 * SOFSEK — Release Console (§36). Committing a sandbox session drafts a
 * versioned Release grouped strictly by application (D12 isolation — a
 * release item carries only its own delta's data, never another
 * application's identifiers). Self-application items are already APPLIED at
 * release time; external items are applied by hand (REPORT) or fetched/
 * acknowledged by the target system itself (PULL, simulated here).
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { RefreshCw, Search } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.RELEASE.LIST';

interface CodeOpt { code: string; name: string; }
interface ReleaseRow {
  release_id: number;
  internal_code: string;
  release_status_code: string;
  released_at: string;
}
interface ItemRow {
  release_item_id: number;
  release_id: number;
  application_id: number;
  description: string | null;
  release_item_status_code: string;
  is_urgent: 'Y' | 'N';
  applied_at: string | null;
}

const GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code', header: 'Release', defaultWidth: 220 },
  { key: 'status', header: 'Status', defaultWidth: 140 },
  { key: 'released_at', header: 'Released', defaultWidth: 170 },
  { key: 'item_count', header: 'Items', defaultWidth: 100 },
  { key: 'outstanding_count', header: 'Outstanding', defaultWidth: 110 },
];

function tlName(list: unknown, fallback: string): string {
  const arr = (list ?? []) as { locale_code: string; display_text: string }[];
  return arr.find(x => x.locale_code === 'EN_US')?.display_text ?? fallback;
}

function ReleaseInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [releases, setReleases] = useState<ReleaseRow[]>([]);
  const [items, setItems] = useState<ItemRow[]>([]);
  const [statuses, setStatuses] = useState<CodeOpt[]>([]);
  const [apps, setApps] = useState<CodeOpt[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<string[]>([]);
  const [drilling, setDrilling] = useState<ReleaseRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [relRes, itemRes, statusRes, appRes] = await Promise.all([
        apiDb.from('sfk_release').select('release_id, internal_code, release_status_code, released_at').order('released_at', { ascending: false }),
        apiDb.from('sfk_release_item').select('release_item_id, release_id, application_id, description, release_item_status_code, is_urgent, applied_at'),
        apiDb.from('sfk_release_status').select('release_status_code, sfk_release_status_tl ( locale_code, display_text )'),
        apiDb.from('csm_application_master').select('application_id, csm_application_master_tl ( locale_code, display_text )'),
      ]);
      if (relRes.error) throw relRes.error;
      if (itemRes.error) throw itemRes.error;
      if (statusRes.error) throw statusRes.error;
      if (appRes.error) throw appRes.error;
      setReleases((relRes.data ?? []) as unknown as ReleaseRow[]);
      setItems((itemRes.data ?? []) as unknown as ItemRow[]);
      setStatuses(((statusRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.release_status_code as string, name: tlName(r.sfk_release_status_tl, r.release_status_code as string) })));
      setApps(((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.application_id), name: tlName(r.csm_application_master_tl, String(r.application_id)) })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => { void reload(); }, []);

  const resolveNameForStatus = (code: string) => statuses.find(s => s.code === code)?.name ?? code;
  const appName = (id: number) => apps.find(a => a.code === String(id))?.name ?? String(id);
  const itemsFor = (releaseId: number) => items.filter(i => i.release_id === releaseId);

  const detail = useExpandedRows<number>();
  // §17.B13 — a release's related children are its per-application release items.
  const buildRelatedGroups = (r: ReleaseRow): RelatedChildGroup[] => [
    { key: 'items', title: t('sfk.release.related.items', 'Release Items'), items: itemsFor(r.release_id).map(i => ({
      id: i.release_item_id,
      label: `${appName(i.application_id)}${i.description ? ` — ${i.description}` : ''}`,
      title: String(i.application_id),
      badges: i.is_urgent === 'Y' ? [t('sfk.release.urgent', 'Urgent')] : undefined,
    })) },
  ];
  const hasRelated = (r: ReleaseRow) => itemsFor(r.release_id).length > 0;

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return releases.filter(r =>
      (statusFacet.length === 0 || statusFacet.includes(r.release_status_code)) &&
      (!q || r.internal_code.toLowerCase().includes(q))
    );
  }, [releases, statusFacet, filter]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.release.title', 'Release Console')}
          description={t('sfk.release.subtitle', 'Versioned releases grouped strictly by application, with per-item application status.')}
          actions={
            <button onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
          }
        />
        <StatStrip stats={[{ value: releases.length, label: t('common.total', 'Total') }, { value: filtered.length, label: t('common.shown', 'Shown') }]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.release.facet.status', 'Status')} options={statuses.map(s => ({ value: s.code, label: s.name }))} selected={statusFacet} onChange={setStatusFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || statusFacet.length > 0} onClear={() => { setFilter(''); setStatusFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
            <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const relItems = itemsFor(r.release_id);
                  const outstanding = relItems.filter(i => i.release_item_status_code === 'OUTSTANDING').length;
                  return (
                    <Fragment key={r.release_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle">
                        <RelatedExpandButton open={detail.isOpen(r.release_id)} hasChildren={hasRelated(r)} onToggle={() => detail.toggle(r.release_id)}
                          expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'internal_code' && r.internal_code}
                          {col.key === 'status' && resolveNameForStatus(r.release_status_code)}
                          {col.key === 'released_at' && formatDateTime(r.released_at)}
                          {col.key === 'item_count' && relItems.length}
                          {col.key === 'outstanding_count' && outstanding}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                        <button onClick={() => setDrilling(r)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.release.view_items', 'View Items')}</button>
                      </td>
                    </tr>
                    {detail.isOpen(r.release_id) && hasRelated(r) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-2">
                          <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records')} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {drilling && (
        <ReleaseItemsModal session={session} release={drilling} items={itemsFor(drilling.release_id)}
          onClose={() => setDrilling(null)}
          onChanged={() => void reload()} />
      )}
    </div>
  );
}

function ReleaseItemsModal({ session, release, items, onClose, onChanged }: {
  session: AuthSession; release: ReleaseRow; items: ItemRow[]; onClose: () => void; onChanged: () => void;
}) {
  // Opened by a drill-down (a read), so the two RPC writes below are gated
  // here. Neither goes through routed*, which is why the affordance gate did
  // not flag this page at all.
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  const { t } = useTranslation();
  const [apps, setApps] = useState<CodeOpt[]>([]);
  const [statuses, setStatuses] = useState<CodeOpt[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      const [appRes, statusRes] = await Promise.all([
        apiDb.from('csm_application_master').select('application_id, csm_application_master_tl ( locale_code, display_text )'),
        apiDb.from('sfk_release_item_status').select('release_item_status_code, sfk_release_item_status_tl ( locale_code, display_text )'),
      ]);
      setApps(((appRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: String(r.application_id), name: tlName(r.csm_application_master_tl, String(r.application_id)) })));
      setStatuses(((statusRes.data ?? []) as unknown as Record<string, unknown>[]).map(r => ({ code: r.release_item_status_code as string, name: tlName(r.sfk_release_item_status_tl, r.release_item_status_code as string) })));
    })();
  }, []);

  const appName = (id: number) => apps.find(a => a.code === String(id))?.name ?? String(id);
  const resolveNameForItemStatus = (code: string) => statuses.find(s => s.code === code)?.name ?? code;

  const markApplied = async (item: ItemRow) => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await apiDb.rpc('sfk_mark_release_item_applied', { p_release_item_id: item.release_item_id });
      if (e) throw new Error(e.message);
      onChanged();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const pullAndAck = async (item: ItemRow) => {
    setBusy(true); setError(null);
    try {
      const { error: pullErr } = await apiDb.rpc('sfk_pull_release_items', { p_application_id: item.application_id });
      if (pullErr) throw new Error(pullErr.message);
      const { error: ackErr } = await apiDb.rpc('sfk_acknowledge_release_item', { p_release_item_id: item.release_item_id });
      if (ackErr) throw new Error(ackErr.message);
      onChanged();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('sfk.release.items_title', `Release Items — ${release.internal_code}`)}
      onClose={onClose}
      width="lg"
      headerAction={<PersonalizationToggle />}
      footer={<button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.close', 'Close')}</button>}
    >
      {error && <div className="mb-2 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
      <table className="w-full text-csm-sm">
        <thead className="text-csm-xs uppercase tracking-wider text-csm-ink-soft">
          <tr>
            <th className="border-b border-csm-border px-2 py-1 text-left">{t('sfk.release.application', 'Application')}</th>
            <th className="border-b border-csm-border px-2 py-1 text-left">{t('sfk.release.description', 'Description')}</th>
            <th className="border-b border-csm-border px-2 py-1 text-left">{t('sfk.release.urgent', 'Urgent')}</th>
            <th className="border-b border-csm-border px-2 py-1 text-left">{t('sfk.release.status', 'Status')}</th>
            <th className="border-b border-csm-border px-2 py-1 text-left">{t('common.actions', 'Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map(i => (
            <tr key={i.release_item_id}>
              <td className="border-b border-csm-border-soft px-2 py-1">{appName(i.application_id)}</td>
              <td className="border-b border-csm-border-soft px-2 py-1">{i.description ?? '—'}</td>
              <td className="border-b border-csm-border-soft px-2 py-1"><YesNoBadge value={i.is_urgent} /></td>
              <td className="border-b border-csm-border-soft px-2 py-1">{resolveNameForItemStatus(i.release_item_status_code)}</td>
              <td className="border-b border-csm-border-soft px-2 py-1">
                {i.release_item_status_code === 'OUTSTANDING' && (
                  <>
                    {canChange && (<button disabled={busy} onClick={() => void markApplied(i)} className="mr-1 rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.release.mark_applied', 'Mark Applied (Report)')}</button>)}
                    {canChange && (<button disabled={busy} onClick={() => void pullAndAck(i)} className="rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('sfk.release.pull_ack', 'Pull & Acknowledge')}</button>)}
                  </>
                )}
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr><td colSpan={5} className="py-6 text-center text-csm-sm text-csm-ink-soft">{t('common.no_records', 'No records')}</td></tr>
          )}
        </tbody>
      </table>
    </Modal>
  );
}

export function SfkReleasePage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <ReleaseInner session={session} />
    </PersonalizationProvider>
  );
}
